import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikValues } from "formik";
import { opsBulkActionSchema } from "formValidations/opsBulkActionSchema";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { useUpdateBulkTaskMutation } from "store/apis/task";
import { BackendError } from "types/utils/error";
import { validate } from "uuid";
import { UNASSIGNED_OPTION } from "./Task1120FilterOptions";

type Task1120BulkServiceTeamUpdateProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  resetSelection: () => void;
  serviceTeamId: string;
};

export const Task1120BulkPreparerUpdateModal = ({
  isOpen,
  close,
  selectedRows,
  resetSelection,
  serviceTeamId,
}: Task1120BulkServiceTeamUpdateProps) => {
  const { isAdmin } = useRoleBasedView();
  const { promise: promiseToast } = useToast();
  const serviceTeamIds = serviceTeamId
    ?.split(",")
    .filter((id) => validate(id))
    .join(",");
  const { data: teamMembers = [], isLoading } = useGetServiceTeamQuery(
    {
      serviceTeamId: serviceTeamIds,
    },
    { skip: !isAdmin }
  );

  const [bulkTaskUpdate, { isLoading: isUpdating }] =
    useUpdateBulkTaskMutation();

  const handleUpdate = async (values: FormikValues) => {
    promiseToast(
      () =>
        bulkTaskUpdate({
          payload: {
            task_ids: selectedRows,
            preparer_id:
              values.value === UNASSIGNED_OPTION ? null : values.value,
          },
        }).unwrap(),
      {
        loading: "Updating preparer...",
        success: (data) => {
          close();
          resetSelection();
          return `Preparer updated for ${selectedRows.length} tasks`;
        },
        error: (error) =>
          (error as BackendError)?.data?.error?.message || "Please try again",
      }
    );
  };

  const options = teamMembers.map((member) => ({
    value: member.uuid,
    label: member.name,
  }));

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Update Preparer</Modal.Title>
          </Modal.Header>

          <Formik
            initialValues={{ value: "" }}
            onSubmit={handleUpdate}
            enableReinitialize
            validationSchema={opsBulkActionSchema}
          >
            {({ handleSubmit }) => {
              return (
                <Form onSubmit={handleSubmit} className="t-m-0">
                  <Modal.Body>
                    {isLoading ? (
                      <Loader size="small" />
                    ) : (
                      <Combobox
                        withForm
                        name="value"
                        label="Select Preparer"
                        required
                        menuPortalTarget={document.body}
                        options={options}
                        placeholder="Select"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            width: "300px",
                          }),
                        }}
                      />
                    )}
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button type="button">Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      type="submit"
                      isLoading={isUpdating}
                    >
                      Assign all ({selectedRows.length})
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              );
            }}
          </Formik>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
